// Interruptor del modo Sandbox, en sus dos sitios: la fila del menú de cuenta
// y la tarjeta de Perfil → Ajustes. Los dos leen el mismo store.

import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Switch } from "../../components/ui";
import { sampleDomains, toggleSandbox, useSandboxStore } from "./sandboxStore";

function useSandbox() {
  const enabled = useSandboxStore((s) => s.enabled);
  const canToggle = useSandboxStore((s) => s.canToggle);
  const busy = useSandboxStore((s) => s.busy);
  const error = useSandboxStore((s) => s.error);
  const domains = useSandboxStore((s) => s.domains);
  const total = useSandboxStore((s) => s.total);
  const load = useSandboxStore((s) => s.load);
  useEffect(() => {
    void load();
  }, [load]);
  return { enabled, canToggle, busy, error, domains, total };
}

/** Fila del menú de cuenta (junto a Tema e Idioma). Quien no puede cambiarlo no la ve. */
export function SandboxMenuRow() {
  const { t } = useTranslation("common");
  const { enabled, canToggle, busy } = useSandbox();
  if (!canToggle) return null;
  return (
    <div className="flex min-h-[44px] items-center justify-between gap-3 px-4">
      <span className="flex items-center gap-3 text-sm text-content-secondary">
        <i className={`${busy ? "ri-loader-4-line animate-spin" : "ri-flask-line"} text-[15px] text-content-muted`} aria-hidden="true" />
        {t("sandbox.title", "Sandbox")}
      </span>
      <Switch
        size="sm"
        checked={enabled}
        disabled={busy}
        onChange={(next) => void toggleSandbox(next)}
        aria-label={t("sandbox.title", "Sandbox")}
      />
    </div>
  );
}

/** Tarjeta de Perfil → Ajustes: explica qué hace y a quién le toca. */
export function SandboxSettingCard() {
  const { t } = useTranslation("common");
  const { enabled, canToggle, busy, error, domains, total } = useSandbox();
  return (
    <div className="flex w-full items-center gap-4 rounded-lg border border-line bg-app p-4" data-testid="sandbox-setting">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500/10">
        <i className="ri-flask-line text-lg text-primary-500" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-content">{t("sandbox.settingTitle", "Modo Sandbox")}</p>
        <p className="text-xs text-content-muted">
          {t(
            "sandbox.settingDesc",
            "Explora una empresa de ejemplo con datos de todas las funcionalidades (flota, recorridos, alertas, envíos, mantenimiento, inspecciones, staff, punto de venta, control de acceso, patios, pasajeros y bolsa de carga). Es de solo lectura: para crear algo, sal del Sandbox y se guarda en tu cuenta.",
          )}
        </p>
        {enabled && total > 0 && (
          <p className="mt-1 text-xs text-content-faint" data-testid="sandbox-setting-summary">
            {t("sandbox.sampleRecords", "{{count}} registros en {{modules}} módulos", { count: total, modules: sampleDomains(domains) })}
          </p>
        )}
        {!canToggle && (
          <p className="mt-1 text-xs text-content-faint" data-testid="sandbox-setting-hint">
            {t("sandbox.unavailable", "El Sandbox no está disponible por ahora.")}
          </p>
        )}
        {busy && (
          <p className="mt-1 text-xs text-content-faint">
            {enabled ? t("sandbox.turningOff", "Saliendo…") : t("sandbox.turningOn", "Entrando…")}
          </p>
        )}
        {error && <p className="mt-1 text-xs text-error">{t("sandbox.error", "No se pudo cambiar. Intenta de nuevo.")}</p>}
      </div>
      <Switch
        checked={enabled}
        disabled={busy || !canToggle}
        onChange={(next) => void toggleSandbox(next)}
        aria-label={t("sandbox.settingTitle", "Modo Sandbox")}
      />
    </div>
  );
}
